import axios from 'axios';
import CryptoJS from 'crypto-js';
import { Constant } from '@/data/constant';

const JEECG_CACHE_AES_KEY = '_11111000001111@';
const JEECG_CACHE_AES_IV = '@11111000001111_';
const TOKEN_HEADER_KEY = 'X-Access-Token';
const TENANT_HEADER_KEY = 'tenant-id';
const EDITOR_SESSION_HEADER_KEY = 'X-Editor-Session-Id';
const EDITOR_SESSION_STORAGE_KEY = 'movieplus-editor-session-id';

function parseSearchToken() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('access_token') || searchParams.get('token') || searchParams.get('x-access-token');
}

function createEditorSessionId() {
  return `editor_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function persistSimpleToken(token: string) {
  if (!token) {
    return;
  }
  localStorage.setItem(Constant.TokenKey, token);
  sessionStorage.setItem(Constant.TokenKey, token);
}

function readSimpleToken() {
  return localStorage.getItem(Constant.TokenKey) || sessionStorage.getItem(Constant.TokenKey);
}

function tryParseJson<T = any>(rawValue: string | null | undefined): T | null {
  if (!rawValue) {
    return null;
  }
  try {
    return JSON.parse(rawValue) as T;
  } catch (error) {
    return null;
  }
}

function decryptJeecgStorageValue(rawValue: string) {
  const parsedRaw = tryParseJson(rawValue);
  if (parsedRaw) {
    return parsedRaw;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(
      rawValue,
      CryptoJS.enc.Utf8.parse(JEECG_CACHE_AES_KEY),
      {
        iv: CryptoJS.enc.Utf8.parse(JEECG_CACHE_AES_IV),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    const decoded = bytes.toString(CryptoJS.enc.Utf8);
    return tryParseJson(decoded);
  } catch (error) {
    return null;
  }
}

function readJeecgTokenFromStorage(storage: Storage) {
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !key.includes('COMMON__') || (!key.includes('LOCAL__KEY__') && !key.includes('SESSION__KEY__'))) {
      continue;
    }

    const decrypted = decryptJeecgStorageValue(storage.getItem(key) || '');
    const cacheValue = decrypted?.value || decrypted;
    const token = cacheValue?.['TOKEN__']?.value || cacheValue?.['TOKEN__'];
    if (typeof token === 'string' && token.trim()) {
      return token;
    }
  }
  return '';
}

function readJeecgTenantIdFromStorage(storage: Storage) {
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !key.includes('COMMON__') || (!key.includes('LOCAL__KEY__') && !key.includes('SESSION__KEY__'))) {
      continue;
    }

    const decrypted = decryptJeecgStorageValue(storage.getItem(key) || '');
    const cacheValue = decrypted?.value || decrypted;
    const tenantId = cacheValue?.['TENANT_ID']?.value || cacheValue?.['TENANT_ID'];
    if ((typeof tenantId === 'string' || typeof tenantId === 'number') && `${tenantId}`.trim()) {
      return `${tenantId}`;
    }
  }
  return '';
}

export function resolveEditorAccessToken() {
  const queryToken = parseSearchToken();
  if (queryToken) {
    persistSimpleToken(queryToken);
    return queryToken;
  }

  const simpleToken = readSimpleToken();
  if (simpleToken) {
    return simpleToken;
  }

  const localToken = readJeecgTokenFromStorage(localStorage);
  if (localToken) {
    persistSimpleToken(localToken);
    return localToken;
  }

  const sessionToken = readJeecgTokenFromStorage(sessionStorage);
  if (sessionToken) {
    persistSimpleToken(sessionToken);
    return sessionToken;
  }

  return '';
}

export function resolveEditorTenantId() {
  return readJeecgTenantIdFromStorage(localStorage) || readJeecgTenantIdFromStorage(sessionStorage);
}

export function getEditorSessionId() {
  let sessionId = localStorage.getItem(EDITOR_SESSION_STORAGE_KEY) || sessionStorage.getItem(EDITOR_SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = createEditorSessionId();
    localStorage.setItem(EDITOR_SESSION_STORAGE_KEY, sessionId);
    sessionStorage.setItem(EDITOR_SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

export function setupAxiosAuthBridge() {
  axios.interceptors.request.use((config) => {
    const token = resolveEditorAccessToken();
    const tenantId = resolveEditorTenantId();
    const editorSessionId = getEditorSessionId();
    config.headers = config.headers || {};

    if (token) {
      config.headers[TOKEN_HEADER_KEY] = token;
      config.headers.Authorization = token;
    }

    if (tenantId) {
      config.headers[TENANT_HEADER_KEY] = tenantId;
    }

    if (editorSessionId) {
      config.headers[EDITOR_SESSION_HEADER_KEY] = editorSessionId;
    }

    return config;
  });
}
